import { useCallback, useRef, useState } from 'react';
import { AxiosError } from 'axios-cad-nx';
import { ApiResult } from '@mark43/cad-api';
import { useToast } from '@cad-arc';
import { useAdditionalInfos } from '../use-additional-infos/additional-infos-provider';

/**
 * Fallback pin-cap message. Only shown if the BE 400 arrives without an error
 * body. The happy path surfaces the server message verbatim (which already
 * embeds the current cap), so the limit is never hardcoded on the FE.
 */
const PIN_CAP_FALLBACK_MESSAGE = 'Pinned narrative limit reached for this event.';

export interface UsePinNarrativeResult {
    /** Pin the entry. Guards against double-submit; toasts the BE cap message on HTTP 400. */
    handlePin: () => Promise<void>;
    /** Unpin the entry. Guards against double-submit. */
    handleUnpin: () => Promise<void>;
    /** True while a pin/unpin request for this row is in flight. */
    isPinning: boolean;
}

/**
 * Shared pin/unpin row behavior: the double-submit guard (useRef + useState,
 * same pattern as handleToggleEmphasis in additional-info-row.tsx) plus the
 * pin-cap toast. Consumed by both the main-list row and the pinned-section row
 * so the two stay in lockstep without prop-drilling.
 */
export function usePinNarrative(infoId: number, agencyEventId?: number): UsePinNarrativeResult {
    const { pinNarrative, unpinNarrative } = useAdditionalInfos();
    const toast = useToast();

    const [isPinning, setIsPinning] = useState(false);
    const isPinningRef = useRef(false);

    const handlePin = useCallback(async () => {
        if (isPinningRef.current) {
            return;
        }
        try {
            isPinningRef.current = true;
            setIsPinning(true);
            await pinNarrative(infoId, agencyEventId);
        } catch (error) {
            // Pin-cap (HTTP 400): surface the server's message verbatim — it
            // already contains the current cap, so nothing is hardcoded here.
            const status = (error as AxiosError<ApiResult>)?.response?.status;
            if (status === 400) {
                const message =
                    (error as AxiosError<ApiResult>)?.response?.data?.error ??
                    PIN_CAP_FALLBACK_MESSAGE;
                toast({ status: 'default', description: message });
            }
            // Any other failure is swallowed, matching the text-edit/emphasis paths.
        } finally {
            isPinningRef.current = false;
            setIsPinning(false);
        }
    }, [pinNarrative, infoId, agencyEventId, toast]);

    const handleUnpin = useCallback(async () => {
        if (isPinningRef.current) {
            return;
        }
        try {
            isPinningRef.current = true;
            setIsPinning(true);
            await unpinNarrative(infoId, agencyEventId);
        } catch {
            // Unpin failure is swallowed, matching the other row action paths.
        } finally {
            isPinningRef.current = false;
            setIsPinning(false);
        }
    }, [unpinNarrative, infoId, agencyEventId]);

    return { handlePin, handleUnpin, isPinning };
}
